/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createLogPrefix, logDebug, type LogTarget, noop } from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import type { AuthTokenProvider } from '@amzn/vinyl-amzn'
import { cblSignIn, type CblSignInDeps } from '@amzn/vinyl-amzn/cbl'
import { type Auth, AuthStatus } from '@/auth/auth'
import { localSignInPersistence } from '@/auth/signInPersistence'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

/**
 * Manages authentication state with code-based linking.
 * Sets the auth token and DMLS options on the player.
 */
export class CblAuth implements Auth, LogTarget {
    logPrefix = createLogPrefix('CblAuth')

    private _status = data(AuthStatus.SIGNED_OUT)
    private _authTokenProvider: AuthTokenProvider | null = null

    get status(): ObservableValue<AuthStatus> {
        return this._status
    }

    constructor(private readonly deps: CblSignInDeps) {}

    isSignedIn(): boolean {
        return this._authTokenProvider != null
    }

    get authTokenProvider(): AuthTokenProvider {
        if (this._authTokenProvider == null) throw new Error('not signed in')
        return this._authTokenProvider
    }

    /**
     * Prompts for sign-in (may use local storage to retrieve previous auth).
     */
    signIn() {
        logDebug(this, 'signIn')
        if (this._status.value !== AuthStatus.SIGNED_OUT) return
        this._status.value = AuthStatus.PENDING
        ;(async () => {
            this._authTokenProvider = await cblSignIn(this.deps)
            logDebug(this, 'signInComplete')
            this._status.value = AuthStatus.SIGNED_IN
        })().catch(createErrorHandler(this))
    }

    /**
     * Signs out, clearing the currently authenticated user.
     * Emits a 'signOut' event.
     */
    signOut() {
        localSignInPersistence.remove().catch(noop)
        this._authTokenProvider = null
        this._status.value = AuthStatus.SIGNED_OUT
    }
}
